import { Component, HostListener, OnInit, inject, signal, ViewChild, ElementRef, effect, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { OmnisearchService, SearchResult } from '../../../../core/services/omnisearch.service';

@Component({
  selector: 'app-omnisearch',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @if (omnisearchService.isOpen()) {
      <div class="fixed inset-0 z-[999999] flex items-start justify-center pt-16 sm:pt-24 px-4 pb-4">
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" (click)="close()"></div>
        
        <!-- Modal Panel -->
        <div class="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 transition-all dark:bg-gray-900 dark:ring-white/10">
          
          <!-- Search Input -->
          <div class="relative flex items-center px-4 py-4 border-b border-gray-100 dark:border-gray-800">
            <svg class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              #searchInput
              type="text"
              class="h-10 w-full bg-transparent pl-4 pr-4 text-gray-900 placeholder-gray-400 focus:outline-none dark:text-white sm:text-lg"
              placeholder="Buscar prospectos, cotizaciones, expedientes..."
              [(ngModel)]="searchQuery"
              (ngModelChange)="onSearchChange($event)"
              (keydown)="handleInputKeyDown($event)"
            />
            <button (click)="close()" class="rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400">
              ESC
            </button>
          </div>

          <!-- Results -->
          <div class="max-h-[60vh] overflow-y-auto p-2" *ngIf="results().length > 0">
            <div *ngFor="let group of groupedResults(); let groupIndex = index" class="mb-4 last:mb-0">
              <h4 class="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {{ group.type }}
              </h4>
              <ul class="mt-1 space-y-1">
                <li *ngFor="let result of group.items; let itemIndex = index">
                  <button
                    (click)="navigate(result)"
                    (mouseenter)="selectedIndex.set(result.absoluteIndex)"
                    [ngClass]="{
                      'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-400': selectedIndex() === result.absoluteIndex,
                      'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.02]': selectedIndex() !== result.absoluteIndex
                    }"
                    class="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors"
                  >
                    <!-- Icon based on type -->
                    <div class="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
                         [ngClass]="{
                           'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400': selectedIndex() === result.absoluteIndex
                         }">
                      <ng-container [ngSwitch]="result.type">
                        <svg *ngSwitchCase="'LEAD'" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                        <svg *ngSwitchCase="'QUOTE'" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        <svg *ngSwitchCase="'DOSSIER'" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                        <svg *ngSwitchDefault class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </ng-container>
                    </div>
                    <div class="flex-auto">
                      <p class="text-sm font-medium">{{ result.title }}</p>
                      <p *ngIf="result.subtitle" class="text-xs opacity-70">{{ result.subtitle }}</p>
                    </div>
                    <svg *ngIf="selectedIndex() === result.absoluteIndex" class="h-4 w-4 flex-none text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </li>
              </ul>
            </div>
          </div>
          
          <!-- Empty State -->
          <div *ngIf="searchQuery.trim() && results().length === 0 && !isSearching" class="px-6 py-14 text-center text-sm sm:px-14">
            <svg class="mx-auto h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="mt-4 font-semibold text-gray-900 dark:text-white">No se encontraron resultados</p>
            <p class="mt-2 text-gray-500">Intenta buscar con otros términos.</p>
          </div>

          <!-- Loading State -->
          <div *ngIf="isSearching" class="px-6 py-14 text-center text-sm sm:px-14">
            <div class="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-brand-500 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            <p class="mt-4 text-gray-500">Buscando...</p>
          </div>

          <!-- Helper Footer -->
          <div class="flex flex-wrap items-center bg-gray-50 dark:bg-gray-800/50 px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400">
            <span class="flex items-center gap-1 mr-4">
              <kbd class="rounded border border-gray-200 bg-white px-1.5 dark:border-gray-700 dark:bg-gray-800">↑</kbd>
              <kbd class="rounded border border-gray-200 bg-white px-1.5 dark:border-gray-700 dark:bg-gray-800">↓</kbd>
              Navegar
            </span>
            <span class="flex items-center gap-1 mr-4">
              <kbd class="rounded border border-gray-200 bg-white px-1.5 dark:border-gray-700 dark:bg-gray-800">Enter</kbd>
              Seleccionar
            </span>
            <span class="flex items-center gap-1">
              <kbd class="rounded border border-gray-200 bg-white px-1.5 dark:border-gray-700 dark:bg-gray-800">Esc</kbd>
              Cerrar
            </span>
          </div>

        </div>
      </div>
    }
  `
})
export class OmnisearchComponent implements OnInit {
  public omnisearchService = inject(OmnisearchService);
  private router = inject(Router);

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  searchQuery = '';
  searchSubject = new Subject<string>();
  isSearching = false;

  results = signal<SearchResult[]>([]);
  selectedIndex = signal(0);

  constructor() {
    effect(() => {
      if (this.omnisearchService.isOpen()) {
        this.searchQuery = '';
        this.results.set([]);
        this.selectedIndex.set(0);
        setTimeout(() => this.searchInput?.nativeElement?.focus(), 100);
      }
    });
  }

  ngOnInit() {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        this.isSearching = true;
        if (!query.trim()) {
          this.isSearching = false;
          return of({ data: [] });
        }
        return this.omnisearchService.search(query).pipe(
          catchError((err) => {
            console.error('Búsqueda fallida:', err);
            this.isSearching = false;
            return of({ data: [] });
          })
        );
      })
    ).subscribe({
      next: (res: any) => {
        // Normalize type to uppercase to avoid mismatches
        const normalizedData = (res.data || []).map((item: any) => ({
          ...item,
          type: item.type ? item.type.toUpperCase() : 'OTHER'
        }));
        // Sort by type to group them correctly
        const sortedData = normalizedData.sort((a: SearchResult, b: SearchResult) => a.type.localeCompare(b.type));
        this.results.set(sortedData);
        this.selectedIndex.set(0);
        this.isSearching = false;
      }
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleGlobalKeyDown(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.omnisearchService.open();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape() {
    if (this.omnisearchService.isOpen()) {
      this.close();
    }
  }

  handleInputKeyDown(event: KeyboardEvent) {
    const itemsCount = this.results().length;
    if (itemsCount === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex.update(i => (i + 1) % itemsCount);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex.update(i => (i - 1 + itemsCount) % itemsCount);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const selectedItem = this.results()[this.selectedIndex()];
      if (selectedItem) {
        this.navigate(selectedItem);
      }
    }
  }

  onSearchChange(query: string) {
    this.searchSubject.next(query);
  }

  close() {
    this.omnisearchService.close();
  }

  navigate(result: SearchResult) {
    if (result.url) {
      // Ensure the URL starts with a slash for absolute routing
      const url = result.url.startsWith('/') ? result.url : '/' + result.url;
      this.router.navigateByUrl(url).then(() => this.close());
    } else {
      // Fallback robusto en caso de que el backend aún no devuelva la propiedad 'url'
      const type = (result.type || '').toUpperCase();
      let route = null;
      
      if (type === 'LEAD') {
        route = ['/leads', result.id];
      } else if (type === 'QUOTE') {
        route = ['/quotes', result.id];
      } else if (type === 'DOSSIER') {
        route = ['/dossiers', result.id];
      }
      
      if (route) {
        this.router.navigate(route).then(() => this.close());
      } else {
        console.warn('Tipo de resultado no soportado para navegación:', result);
        this.close();
      }
    }
  }

  groupedResults = computed(() => {
    const currentResults = this.results();
    if (!currentResults.length) return [];

    const groups: { type: string, items: (SearchResult & { absoluteIndex: number })[] }[] = [];
    const typeMap = new Map<string, (SearchResult & { absoluteIndex: number })[]>();
    
    let absoluteIndex = 0;
    for (const result of currentResults) {
       let list = typeMap.get(result.type);
       if (!list) {
          list = [];
          typeMap.set(result.type, list);
          groups.push({ type: result.type, items: list });
       }
       list.push({ ...result, absoluteIndex: absoluteIndex++ });
    }

    return groups;
  });
}
